import type { Route } from 'nextjs-routes';

// if not local, use relative URLs
export const BASE_URL = process.env.NEXT_PUBLIC_LOCAL_BASE_URL ?? '';

export const ROUTES = {
  LISTING: `/property/[listingAlias]`,
  APPLICATION: `/property/[listingAlias]/application/[applicationId]`,
  DECLINE_APPLICATION: `/property/[listingAlias]/application/[applicationId]/decline`,
  APPLICANT: `/property/[listingAlias]/application/[applicationId]/applicants/[applicantUserId]`,
  CREDIT_REPORT: `/property/[listingAlias]/application/[applicationId]/reports/[applicantUserId]/creditreport`,
  BACKGROUND_CHECK: `/property/[listingAlias]/application/[applicationId]/reports/[applicantUserId]/backgroundcheck`,
  ZILLOW_TENANT_SCREENING_SERVICES: '/tenant-screening-services',
  SHARE: `/share/[shareId]`,
  ID_VERIFICATION: `/id-verification`,
  MISSING_PERMISSIONS: `/missing-permissions`,
} as const;

export const LEGACY_ROUTES = {
  INDEX: '/',
  APPLICATION: `/[applicationId]`,
  APPLICANT: `/[applicationId]/applicants/[zgAuthZone]/[applicantUserId]`,
  CREDIT_REPORT: `/[applicationId]/applicants/[zgAuthZone]/[applicantUserId]/screening/creditreport`,
  BACKGROUND_CHECK: `/[applicationId]/applicants/[zgAuthZone]/[applicantUserId]/screening/backgroundcheck`,
} as const;

export const API = {
  POST_INVITATION: `/rental-manager-api/api/v1/users/capps/handleApplicationInvitation`,
  GET_INVITE_LINK: `/rental-manager-api/api/v1/users/capps/handleApplicationInvitationLink`,
  SET_APPLICATIONS_ACCEPTED: `/rental-manager-api/api/v1/users/properties/applicationsAccepted`,
  GET_SHARED_APPLICATION: `/rental-manager-api/api/web/v1/users/capps/getRentalApplicationShareForLandlord`,
  GET_LISTING_EXISTS_IN_LISTING_HUB: '/rental-manager-api/api/web/v1/properties/getListingExistsInListingHub',
  ACCEPT_APPLICATION_SHARE: '/rental-manager-api/api/web/v1/users/capps/acceptRentalApplicationShare',
  DECLINE_APPLICATION_SHARE: '/rental-manager-api/api/web/v1/users/capps/declineRentalApplicationShare',
  GET_GOOGLE_MAPS_SIGNED_URL: '/rental-manager-api/api/v1/multibrandSupport/googleMaps',
  GET_ADDRESS_AUTOCOMPLETE: '/rental-manager-api/api/v1/properties/addresses/autocomplete',
};

export const EXTERNAL_URLS = {
  PROPERTIES: `${BASE_URL}/rental-manager/properties`,
  SELECT_PROPERTY: `${BASE_URL}/rental-manager/select-property?type=applications`,
} as const;

export const URLS = {
  MARKETING_LANDING_PAGE: 'https://www.zillow.com/z/rental-manager/tenant-screening/',
  FAQS: 'https://www.zillow.help/categories/Renter_Landlord_Property_Manager%2FRenter%2FApplications_screening',
  SAMPLE_ONLINE_APPLICATION:
    'https://wp-tid.zillowstatic.com/bedrock/app/uploads/sites/2/2020/12/Sample-report-e66a3d.png',
  SAMPLE_BACKGROUND_CHECK:
    'https://wp-tid.zillowstatic.com/bedrock/app/uploads/sites/2/2021/12/CIC-Report-810-2x-for-Marketing-2dca38.png',
  SAMPLE_CREDIT_REPORT: 'https://wp.zillowstatic.com/26/credit-report-sample-b90c6f.png',
  COMPLIANCE_LEARN_MORE: 'https://www.zillow.com/z/rental-manager/limits-rental-applications-reports/',
  ICRAA: 'https://www.zillow.help/article/icraa-overview-zd30234464716179',
  VOUCHER_FAQ_LINK: 'https://www.zillow.com/rentals-network/housing-choice-vouchers/',
};

export function getListingRoute({ listingAlias }: { listingAlias: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.LISTING,
    query: {
      listingAlias,
    },
  };
}

export function getApplicationRoute({ listingAlias, applicationId }: { listingAlias: string; applicationId: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.APPLICATION,
    query: {
      listingAlias,
      applicationId,
    },
  };
}

export function getApplicantRoute({
  listingAlias,
  applicationId,
  applicantUserId,
}: {
  listingAlias: string;
  applicationId: string;
  applicantUserId: string;
}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.APPLICANT,
    query: {
      listingAlias,
      applicationId,
      applicantUserId,
    },
  };
}

export function getCreditReportRoute({
  listingAlias,
  applicationId,
  applicantUserId,
}: {
  listingAlias: string;
  applicationId: string;
  applicantUserId: string;
}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.CREDIT_REPORT,
    query: {
      listingAlias,
      applicationId,
      applicantUserId,
    },
  };
}

export function getBackgroundCheckRoute({
  listingAlias,
  applicationId,
  applicantUserId,
}: {
  listingAlias: string;
  applicationId: string;
  applicantUserId: string;
}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.BACKGROUND_CHECK,
    query: {
      listingAlias,
      applicationId,
      applicantUserId,
    },
  };
}

export function getLeasesRoute({ listingAlias, applicationId }: { listingAlias: string; applicationId: string }) {
  return `${BASE_URL}/rental-manager/leasing/property/${listingAlias}/onboarding/createLease?applicationId=${applicationId}`;
}

export function getPaymentsPath({ listingAlias }: { listingAlias: string }) {
  return `${BASE_URL}/rental-manager/payments/properties/${listingAlias}`;
}

export function getMessageConversationRoute({
  listingAlias,
  conversationId,
}: {
  listingAlias: string;
  conversationId: string;
}) {
  return `${BASE_URL}/rental-manager/inbox/${listingAlias}/${conversationId}`;
}

export function getShareRoute({ shareId }: { shareId: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: ROUTES.SHARE,
    query: {
      shareId,
    },
  };
}

export function getMissingPermissionsRoute(): { pathname: Route['pathname']; query: Route['query'] } {
  return {
    pathname: ROUTES.MISSING_PERMISSIONS,
    query: {},
  };
}
